import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import * as Constants from '@/services/Constants'
import useMediaSourceViews from '@/actions/useMediaSourceViews'
import { useUserContext } from '@/UserContext'
import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'
import HistogramIcon from '@/CustomIcons/HistogramIcon'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { AudioQualitySelect } from '@/components/AudioQualitySelect'
import { ControlButton } from '@/UIComponents2/ControlButton'
import GearIcon from '@/CustomIcons/GearIcon'
import { Tutorial } from '../../Tutorial'
import { BrowserAlert } from '../../BrowserAlert'
import { useTranslation } from 'react-i18next'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import { env } from '@/services/EnvironmentVariables'
import { Alert } from '@/UIComponents2/Alert'
import { Button } from '@/UIComponents2/Button'
import { openAudioMIDISetup } from '@/utils'
import ElectronIPC from '@/services/ElectronIPC'
import { EnabledIndicator } from '@/components/EnabledIndicator'
import dawCoverJpg from '@/assets/tutorials/daw-cover.jpg'

export function DawAudioSection() {
    const [userState] = useUserContext()
    const { audioInputsClassifiedForUi } = useMediaSourceViews()
    const { toggleAudioDeviceSelection, updateSelectedAudioDeviceQualityPreset } = useMediaSelectionActions()

    const dawBridgeInput = audioInputsClassifiedForUi['museDawBridge'][0]
    const selection = userState.mediaSelection.audioInputs.find(
        (inp) => inp.source.isMuseDAWBridge && dawBridgeInput?.deviceId === inp.deviceId
    )
    const currQualityPresetCode = selection ? Constants.settingsToAudioQualityPesetCode(selection.settings) : 'enhanced'

    const handleChangeQuality = (code: Constants.AudioQualityPresetCode) => {
        if (selection) {
            updateSelectedAudioDeviceQualityPreset(selection, code)
        }
    }

    const handleToggle = () => {
        if (dawBridgeInput === undefined) {
            return
        }
        toggleAudioDeviceSelection(dawBridgeInput)
    }

    return (
        <Card className="md:p-[10px] flex flex-col gap-[10px]" id="audio-daw">
            <div className="grid grid-cols[1fr] md:grid-cols-2 gap-[30px]">
                <div className="flex flex-col justify-between pl-[20px] py-[15px] gap-4">
                    <div className="flex justify-between">
                        <CardHeader icon={HistogramIcon} title="DAW Audio" />
                        <EnabledIndicator enabled={selection !== undefined} />
                    </div>
                    {dawBridgeInput && (
                        <div className="py-[10px] px-4 border-muse-toplit shadow-glow rounded-[14px] bg-white/3 cursor-pointer">
                            <Checkbox
                                label={dawBridgeInput?.displayLabel ?? 'Missing Virtual Device'}
                                checked={selection !== undefined}
                                onChange={() => {
                                    handleToggle()
                                }}
                            />
                        </div>
                    )}
                    <ErrorAlerts />
                    <div>
                        <div className="pb-2">Quality</div>
                        <div className="grid grid-cols-[1fr_min-content] gap-2">
                            <AudioQualitySelect
                                disabled={selection === undefined}
                                value={currQualityPresetCode}
                                onChange={(code) => handleChangeQuality(code)}
                            />
                            <ControlButton variant="glow" disabled={selection === undefined}>
                                <GearIcon />
                            </ControlButton>
                        </div>
                    </div>
                </div>
                <Tutorial
                    coverImageUrl={dawCoverJpg}
                    title="Share DAW Audio"
                    source={
                        env.OS === 'win32'
                            ? 'https://museapp.sfo3.cdn.digitaloceanspaces.com/in-app-tutorials/daw-win.mp4'
                            : 'https://museapp.sfo3.cdn.digitaloceanspaces.com/in-app-tutorials/daw-mac.mp4'
                    }
                    sourceType="video/mp4"
                />
            </div>
        </Card>
    )
}

function ErrorAlerts() {
    const { t } = useTranslation()

    const [userState] = useUserContext()
    const { refreshMediaSourcesWithDebounce } = useMediaSourceActions()
    const { audioInputsClassifiedForUi } = useMediaSourceViews()
    const museDawBridge = audioInputsClassifiedForUi.museDawBridge
    const status = userState.mediaPreflightStatus.audioDaw

    if (!env.IS_ELECTRON) {
        return <BrowserAlert action="share DAW audio" />
    }

    return (
        <div>
            {/* error message - muse plugin not detected (only show after museDawBridge is properly configured) */}
            {museDawBridge.length > 0 && status.isReady === false && (
                <Alert variant="solid" level="warning">
                    {t(
                        'Muse Plugin not detected. To share DAW Audio, add the Muse Plugin to the master track of your DAW.'
                    )}
                </Alert>
            )}

            {/* error message - no audio drivers detected */}
            {museDawBridge.length === 0 && (
                <Alert variant="solid" level="warning">
                    <div className="flex flex-col gap-2">
                        {env.OS === 'win32'
                            ? t('VB Audio Cable not detected. To share DAW Audio, you need to install VB Audio Cable.')
                            : t(
                                  'Muse DAW Bridge not detected. Please try restarting your computer or reinstalling Muse.'
                              )}
                        {env.OS === 'win32' && (
                            <Button
                                variant="link-dark"
                                onClick={() =>
                                    ElectronIPC.openLinkInBrowser(Constants.links.vb_audio_cable_download_link)
                                }
                            >
                                {t('Download VB Audio Cable')}
                            </Button>
                        )}
                    </div>
                </Alert>
            )}

            {/* error message - device muted */}
            {env.OS === 'darwin' && userState.mediaSourceStatus.audioInputs['museDawBridge']?.isMuted && (
                <Alert variant="solid" level="warning">
                    <div className="flex flex-col gap-2">
                        {t('Looks like Muse DAW Bridge is muted. You can unmute this device in Audio MIDI Setup.')}

                        <Button variant="link" onClick={() => openAudioMIDISetup()}>
                            {t('Open Audio MIDI Setup')}
                        </Button>
                        <Button variant="link" onClick={refreshMediaSourcesWithDebounce}>
                            {t('Check again')}
                        </Button>
                    </div>
                </Alert>
            )}
        </div>
    )
}
